import { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, differenceInDays, format, parseISO } from 'date-fns';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import {
  AppBottomSheet,
  AppBottomSheetHeader,
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppFieldGroup,
  AppInput,
  AppInlineAlert,
  AppLoadingState,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import AppDateField from '@/components/AppDateField';

type HealthWomensHealthRow = Database['public']['Tables']['health_womens_health_trackers']['Row'];

const NHS_MENOPAUSE_URL = 'https://www.nhs.uk/conditions/menopause/';

function notifyHealthRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

function getReminderBadge(dateStr: string | null) {
  if (!dateStr) return { text: 'No reminder date', classes: 'bg-muted text-muted-foreground' };
  const days = differenceInDays(parseISO(dateStr), new Date());
  if (days < 0) return { text: `${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} overdue`, classes: 'bg-red-100 text-red-800' };
  if (days < 14) return { text: `${days} day${days !== 1 ? 's' : ''}`, classes: 'bg-amber-100 text-amber-800' };
  return { text: `${days} day${days !== 1 ? 's' : ''}`, classes: 'bg-green-100 text-green-800' };
}

function estimateNextCycleDate(lastPeriodDate: string | null, cycleLengthDays: number | null) {
  if (!lastPeriodDate || !cycleLengthDays) return null;
  return addDays(parseISO(lastPeriodDate), cycleLengthDays);
}

function formatSavedDate(dateStr: string | null) {
  return dateStr ? format(parseISO(dateStr), 'd MMM yyyy') : 'Not saved';
}

function HealthWomensHealthForm({
  tracker,
  onClose,
  onSaved,
}: {
  tracker: HealthWomensHealthRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useApp();
  const [lastPeriodDate, setLastPeriodDate] = useState<Date | undefined>(
    tracker?.last_period_date ? parseISO(tracker.last_period_date) : undefined,
  );
  const [cycleLengthDays, setCycleLengthDays] = useState(
    tracker?.typical_cycle_length_days != null ? String(tracker.typical_cycle_length_days) : '',
  );
  const [lastSmearDate, setLastSmearDate] = useState<Date | undefined>(
    tracker?.last_smear_date ? parseISO(tracker.last_smear_date) : undefined,
  );
  const [contraceptionMethod, setContraceptionMethod] = useState(tracker?.contraception_method || '');
  const [contraceptionReminderDate, setContraceptionReminderDate] = useState<Date | undefined>(
    tracker?.contraception_reminder_date ? parseISO(tracker.contraception_reminder_date) : undefined,
  );
  const [wantsPerimenopauseResources, setWantsPerimenopauseResources] = useState(
    tracker?.wants_perimenopause_resources || false,
  );
  const [notes, setNotes] = useState(tracker?.notes || '');
  const [saving, setSaving] = useState(false);

  const hasMeaningfulValue = Boolean(
    lastPeriodDate
    || cycleLengthDays.trim()
    || lastSmearDate
    || contraceptionMethod.trim()
    || contraceptionReminderDate
    || wantsPerimenopauseResources
    || notes.trim(),
  );

  const handleSave = async () => {
    if (!user || !hasMeaningfulValue) return;

    const parsedCycleLength = cycleLengthDays.trim() ? Number(cycleLengthDays.trim()) : null;
    if (parsedCycleLength !== null && (!Number.isFinite(parsedCycleLength) || parsedCycleLength < 15 || parsedCycleLength > 60)) {
      toast.error('Cycle length needs to be between 15 and 60 days.');
      return;
    }

    setSaving(true);

    const payload = {
      user_id: user.id,
      last_period_date: lastPeriodDate ? format(lastPeriodDate, 'yyyy-MM-dd') : null,
      typical_cycle_length_days: parsedCycleLength,
      last_smear_date: lastSmearDate ? format(lastSmearDate, 'yyyy-MM-dd') : null,
      contraception_method: contraceptionMethod.trim() || null,
      contraception_reminder_date: contraceptionReminderDate ? format(contraceptionReminderDate, 'yyyy-MM-dd') : null,
      wants_perimenopause_resources: wantsPerimenopauseResources,
      notes: notes.trim() || null,
    };

    const { error } = await supabase
      .from('health_womens_health_trackers')
      .upsert(payload as any, { onConflict: 'user_id' });

    if (error) {
      console.error(error);
      toast.error("Could not save the women's health tracker right now.");
      setSaving(false);
      return;
    }

    setSaving(false);
    onSaved();
  };

  return (
    <AppBottomSheet onClose={onClose}>
      <AppBottomSheetHeader
        title={tracker ? "Edit women's health tracker" : "Add women's health tracker"}
        subtitle="Private to Health only. Adulting does not use this data in benefits, finance, or community."
      />

      <AppCardBody className="flex-1 overflow-y-auto px-5 py-5" gap="$lg">
            <div className="grid gap-4 sm:grid-cols-2">
              <AppDateField
                label="Last period date"
                value={lastPeriodDate ? format(lastPeriodDate, 'yyyy-MM-dd') : ''}
                onChange={(nextValue) => setLastPeriodDate(nextValue ? parseISO(nextValue) : undefined)}
                placeholder="Pick a date"
                helper="Optional. Adulting uses this only for a private next-cycle estimate."
              />

              <AppFieldGroup
                label="Typical cycle length"
                helper="Optional. Save a rough number of days if you want the estimate shown."
              >
                <AppInput
                  tag="input"
                  inputMode="numeric"
                  value={cycleLengthDays}
                  onChange={(event) => setCycleLengthDays((event.target as HTMLInputElement).value)}
                  placeholder="e.g. 28"
                />
              </AppFieldGroup>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <AppDateField
                label="Last cervical screening date"
                value={lastSmearDate ? format(lastSmearDate, 'yyyy-MM-dd') : ''}
                onChange={(nextValue) => setLastSmearDate(nextValue ? parseISO(nextValue) : undefined)}
                placeholder="Pick a date"
                helper="Save the last date here if you want it handy. Keep actual NHS reminder timing in the main reminders list."
              />

              <AppDateField
                label="Contraception reminder date"
                value={contraceptionReminderDate ? format(contraceptionReminderDate, 'yyyy-MM-dd') : ''}
                onChange={(nextValue) => setContraceptionReminderDate(nextValue ? parseISO(nextValue) : undefined)}
                placeholder="Pick a date"
                helper="Use this for a pill, injection, coil check, implant review, or whatever timing matters to you."
              />
            </div>

            <AppFieldGroup
              label="Contraception method"
              helper="Optional. Save only what helps you recognise the reminder."
            >
              <AppInput
                tag="input"
                value={contraceptionMethod}
                onChange={(event) => setContraceptionMethod((event.target as HTMLInputElement).value)}
                placeholder="e.g. Combined pill, copper coil, implant"
              />
            </AppFieldGroup>

            <AppFieldGroup
              label="Perimenopause awareness resources"
              helper="Turn this on if you want the official NHS menopause resources kept visible in this private Health card."
            >
              <AppButtonRow>
                <AppButton
                  size="sm"
                  tone={wantsPerimenopauseResources ? 'primary' : 'outline'}
                  onPress={() => setWantsPerimenopauseResources(true)}
                >
                  Show resources
                </AppButton>
                <AppButton
                  size="sm"
                  tone={!wantsPerimenopauseResources ? 'primary' : 'outline'}
                  onPress={() => setWantsPerimenopauseResources(false)}
                >
                  Not now
                </AppButton>
              </AppButtonRow>
            </AppFieldGroup>

            <AppFieldGroup
              label="Private notes"
              helper="Optional. Keep this only for yourself."
            >
              <AppTextArea
                value={notes}
                onChange={(event) => setNotes((event.target as HTMLTextAreaElement).value)}
                placeholder="Symptoms, questions for your GP, or anything else you want kept private in Health"
                rows={3}
              />
            </AppFieldGroup>

            <AppButtonRow justifyContent="flex-end">
              <AppButton tone="outline" onPress={onClose}>
                Cancel
              </AppButton>
              <AppButton onPress={handleSave} disabled={!hasMeaningfulValue || saving}>
                {saving ? 'Saving…' : tracker ? 'Save changes' : 'Save tracker'}
              </AppButton>
            </AppButtonRow>
      </AppCardBody>
    </AppBottomSheet>
  );
}

export default function HealthWomensHealthSection() {
  const { user } = useApp();
  const [tracker, setTracker] = useState<HealthWomensHealthRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fetchTracker = useCallback(async () => {
    if (!user) {
      setTracker(null);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('health_womens_health_trackers')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error(error);
      toast.error("Could not load the women's health tracker right now.");
      setTracker(null);
      setLoading(false);
      return;
    }

    setTracker((data as HealthWomensHealthRow | null) || null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchTracker();
  }, [fetchTracker]);

  const estimatedNextCycle = useMemo(
    () => estimateNextCycleDate(tracker?.last_period_date || null, tracker?.typical_cycle_length_days || null),
    [tracker?.last_period_date, tracker?.typical_cycle_length_days],
  );

  const reminderBadge = useMemo(
    () => getReminderBadge(tracker?.contraception_reminder_date || null),
    [tracker?.contraception_reminder_date],
  );

  const handleSaved = () => {
    setFormOpen(false);
    setConfirmDelete(false);
    notifyHealthRefresh();
    void fetchTracker();
  };

  const handleDelete = async () => {
    if (!tracker) return;
    const { error } = await supabase.from('health_womens_health_trackers').delete().eq('id', tracker.id);
    if (error) {
      console.error(error);
      toast.error("Could not remove the women's health tracker right now.");
      return;
    }
    setConfirmDelete(false);
    setFormOpen(false);
    notifyHealthRefresh();
    void fetchTracker();
  };

  if (loading) {
    return <AppLoadingState title="Loading women's health tracker" body="Pulling your private women's health detail." />;
  }

  return (
    <div className="space-y-3">
      <AppInlineAlert title="Private to Health only">
        Adulting keeps this tracker inside Health and does not use it in benefits, finance, or community data.
      </AppInlineAlert>

      {tracker ? (
        <AppCard>
          <div className="space-y-3">
            <div>
              <AppText fontSize="$sm" fontWeight="700">Cycle tracking</AppText>
              <AppText fontSize="$sm" color="$colorMuted">
                Last period: {formatSavedDate(tracker.last_period_date)}
              </AppText>
              {tracker.typical_cycle_length_days ? (
                <AppText fontSize="$sm" color="$colorMuted">
                  Typical cycle: {tracker.typical_cycle_length_days} days
                </AppText>
              ) : null}
              {estimatedNextCycle ? (
                <AppText fontSize="$sm" color="$colorMuted">
                  Estimated next cycle: {format(estimatedNextCycle, 'd MMM yyyy')}
                </AppText>
              ) : (
                <AppText fontSize="$sm" color="$colorMuted">
                  Save both a last period date and a typical cycle length if you want the estimate shown here.
                </AppText>
              )}
            </div>

            <div>
              <AppText fontSize="$sm" fontWeight="700">Cervical screening</AppText>
              <AppText fontSize="$sm" color="$colorMuted">
                Last date saved: {formatSavedDate(tracker.last_smear_date)}
              </AppText>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <AppText fontSize="$sm" fontWeight="700">Contraception</AppText>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${reminderBadge.classes}`}>
                  {reminderBadge.text}
                </span>
              </div>
              <AppText fontSize="$sm" color="$colorMuted">
                Method: {tracker.contraception_method || 'Not saved'}
              </AppText>
              <AppText fontSize="$sm" color="$colorMuted">
                Reminder date: {formatSavedDate(tracker.contraception_reminder_date)}
              </AppText>
            </div>

            <div>
              <AppText fontSize="$sm" fontWeight="700">Perimenopause awareness</AppText>
              <AppText fontSize="$sm" color="$colorMuted">
                {tracker.wants_perimenopause_resources
                  ? 'Official NHS menopause resources are kept visible here.'
                  : 'Resources are currently hidden.'}
              </AppText>
              {tracker.wants_perimenopause_resources ? (
                <AppButtonRow mt="$xs">
                  <a
                    href={NHS_MENOPAUSE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground"
                  >
                    NHS menopause advice
                  </a>
                </AppButtonRow>
              ) : null}
            </div>

            {tracker.notes ? (
              <div>
                <AppText fontSize="$sm" fontWeight="700">Private notes</AppText>
                <AppText fontSize="$sm" color="$colorMuted">
                  {tracker.notes}
                </AppText>
              </div>
            ) : null}
          </div>

          <AppButtonRow mt="$sm">
            <AppButton size="sm" tone="outline" onPress={() => { setConfirmDelete(false); setFormOpen(true); }}>
              <Pencil size={12} /> Edit
            </AppButton>
            {confirmDelete ? (
              <>
                <AppButton size="sm" tone="destructive" onPress={handleDelete}>
                  Confirm
                </AppButton>
                <AppButton size="sm" tone="outline" onPress={() => setConfirmDelete(false)}>
                  Cancel
                </AppButton>
              </>
            ) : (
              <AppButton size="sm" tone="outline" onPress={() => setConfirmDelete(true)}>
                <Trash2 size={12} /> Delete
              </AppButton>
            )}
          </AppButtonRow>
        </AppCard>
      ) : (
        <AppEmptyState
          title="No women's health tracker yet"
          body="Keep cycle dates, a last smear date, contraception timing, and perimenopause resources in one private Health card."
          action={(
            <AppButton onPress={() => setFormOpen(true)}>
              <Plus size={14} /> Add private tracker
            </AppButton>
          )}
        />
      )}

      {formOpen ? (
        <HealthWomensHealthForm
          tracker={tracker}
          onClose={() => { setFormOpen(false); setConfirmDelete(false); }}
          onSaved={handleSaved}
        />
      ) : null}
    </div>
  );
}
